import type { User } from "./src/base";
import { accounting } from "./src/modules/accounting";
import { actionable } from "./src/modules/actionable";
import { activityLog } from "./src/modules/activityLog";
import { adjuster } from "./src/modules/adjuster";
import { adminTools } from "./src/modules/adminTools";
import { agentAction } from "./src/modules/agentAction";
import { ahj } from "./src/modules/ahj";
import { aiBidAnalysis, aiChat, aiFeedback, aiFileSummary, aiSummary } from "./src/modules/ai";
import { appeasement } from "./src/modules/appeasement";
import { assessment } from "./src/modules/assessment";
import { automationUi } from "./src/modules/automationUi";
import { bid } from "./src/modules/bid";
import { bidContact } from "./src/modules/bidContact";
import { bidProperties } from "./src/modules/bidProperties";
import { bidRequest } from "./src/modules/bidRequest";
import { bidScoring } from "./src/modules/bidScoring";
import { bidTable } from "./src/modules/bidTable";
import { bill, billReview } from "./src/modules/bill";
import { blockingTask } from "./src/modules/blockingTask";
import { buyerData } from "./src/modules/buyerData";
import { calendar } from "./src/modules/calendar";
import { certificateOfCompletion } from "./src/modules/certificateOfCompletion";
import { changeOrder } from "./src/modules/changeOrder";
import { channel } from "./src/modules/channel";
import { checklist } from "./src/modules/checklist";
import { colorSelection } from "./src/modules/colorSelection";
import { complianceEvent } from "./src/modules/complianceEvent";
import { contractorDocumentExemption } from "./src/modules/contractor-document-exemption";
import { contractorLicense } from "./src/modules/contractor-license";
import { contractorInternalDetailFields } from "./src/modules/contractorDetails";
import { contractorOwners } from "./src/modules/contractorOwners";
import { contractorDocuments, contractorInternalFields, contractorProfile } from "./src/modules/contractorProfile";
import { contractorTraining } from "./src/modules/contractor-training";
import { conversation } from "./src/modules/conversation";
import { conversationAssignment } from "./src/modules/conversationAssignment";
import { conversationClose } from "./src/modules/conversationClose";
import { conversationSubscription } from "./src/modules/conversationSubscription";
import { county } from "./src/modules/county";
import { creditMemo } from "./src/modules/creditMemo";
import { deadline } from "./src/modules/deadline";
import { debug } from "./src/modules/debug";
import { dropboxSign } from "./src/modules/dropboxSign";
import { escalation } from "./src/modules/escalation";
import { escalationHub } from "./src/modules/escalationHub";
import { estimate } from "./src/modules/estimate";
import { externalContractor } from "./src/modules/externalContractor";
import { file } from "./src/modules/file";
import { fileAnnotation } from "./src/modules/file-annotation";
import { finalInspection } from "./src/modules/finalInspection";
import { financeAdminTools } from "./src/modules/financeAdminTools";
import { funding } from "./src/modules/funding";
import { geographicArea } from "./src/modules/geographic-area";
import { globalAppSettings } from "./src/modules/globalAppSettings";
import { hic } from "./src/modules/hic";
import { holdup } from "./src/modules/holdup";
import { homeownerPortal } from "./src/modules/homeownerPortal";
import { homeownerSentiment } from "./src/modules/homeownerSentiment";
import { hubspot } from "./src/modules/hubspot";
import { impersonation } from "./src/modules/impersonation";
import { inbox } from "./src/modules/inbox";
import { insurance } from "./src/modules/insurance";
import { invoice } from "./src/modules/invoice";
import { jobDetails } from "./src/modules/jobDetails";
import { journalEntry } from "./src/modules/journalEntry";
import { lineItemPrice } from "./src/modules/lineItemPrice";
import { link } from "./src/modules/link";
import { livePhotoReview } from "./src/modules/livePhotoReview";
import { loanRateSheet } from "./src/modules/loanRateSheet";
import { market } from "./src/modules/market";
import { marketingLead } from "./src/modules/marketingLead";
import { materials } from "./src/modules/materials";
import { mcp } from "./src/modules/mcp";
import { measurementReport } from "./src/modules/measurementReport";
import { message } from "./src/modules/message";
import { navigation } from "./src/modules/navigation";
import { notificationSettings } from "./src/modules/notificationSettings";
import { notificationList, notifications } from "./src/modules/notifications";
import { ntp } from "./src/modules/ntp";
import { opsAssignment } from "./src/modules/opsAssignment";
import { organization, organizationSettings } from "./src/modules/organization";
import { partnerFlow } from "./src/modules/partnerFlow";
import { permission } from "./src/modules/permission";
import { permittingEntity } from "./src/modules/permittingEntity";
import { person } from "./src/modules/person";
import { personGroup } from "./src/modules/personGroup";
import { personToContractor } from "./src/modules/personToContractor";
import { phaseDuration } from "./src/modules/phaseDuration";
import { phoneProvider } from "./src/modules/phoneProvider";
import { project, projectStatus, projectTable } from "./src/modules/project";
import { projectCategory } from "./src/modules/projectCategory";
import { projectCheckIn } from "./src/modules/projectCheckIn";
import { projectChecklistSchema } from "./src/modules/projectChecklistSchema";
import { projectMilestones } from "./src/modules/projectMilestones";
import { propertyAddress } from "./src/modules/propertyAddress";
import { proxyPhone } from "./src/modules/proxyPhone";
import { quote } from "./src/modules/quote";
import { quoteTab } from "./src/modules/quoteTab";
import { recruitingTriage } from "./src/modules/recruitingTriage";
import { laborbook, ratebook } from "./src/modules/ratebook";
import { receipt } from "./src/modules/receipt";
import { region } from "./src/modules/region";
import { regionRole } from "./src/modules/regionRole";
import { remittance } from "./src/modules/remittance";
import { requestForQuote } from "./src/modules/requestForQuote";
import { review } from "./src/modules/review";
import { reviewBidGuide } from "./src/modules/reviewBidGuide";
import { reviewRevenueAutoComplete } from "./src/modules/reviewRevenueAutoComplete";
import { roofScope } from "./src/modules/roofScope";
import { salesDetails } from "./src/modules/salesDetails";
import { scopeOfWorkType } from "./src/modules/scopeOfWorkType";
import { serviceArea } from "./src/modules/serviceArea";
import { serviceAreaCapacity } from "./src/modules/serviceAreaCapacity";
import { serviceAreaPause } from "./src/modules/serviceAreaPause";
import { smsMessage } from "./src/modules/smsMessage";
import { source } from "./src/modules/source";
import { sowChange } from "./src/modules/sowChange";
import { sunrunAutomation } from "./src/modules/sunrunAutomation";
import { tag } from "./src/modules/tag";
import { deletedTask, task, taskDueDate } from "./src/modules/task";
import { taskAssignee } from "./src/modules/taskAssignee";
import { taskReset } from "./src/modules/taskReset";
import { taskStatus } from "./src/modules/taskStatus";
import { ticket } from "./src/modules/ticket";
import { user } from "./src/modules/user";
import { vendorCredit } from "./src/modules/vendorCredit";
import { verifyContractorCostsAutoComplete } from "./src/modules/verifyContractorCostsAutoComplete";
import { view } from "./src/modules/view";
import { viewList } from "./src/modules/viewList";
import { workflow } from "./src/modules/workflow";
import { workOrder } from "./src/modules/workOrder";
import { worksite } from "./src/modules/worksite";
import { worksiteView } from "./src/modules/worksiteView";

export const can = {
  accounting,
  actionable,
  agentAction,
  adjuster,
  adminTools,
  ahj,
  aiSummary,
  aiChat,
  aiBidAnalysis,
  aiFeedback,
  aiFileSummary,
  appeasement,
  assessment,
  automationUi,
  activityLog,
  bid,
  bidContact,
  bidProperties,
  bidRequest,
  bidScoring,
  bidTable,
  bill,
  billReview,
  blockingTask,
  buyerData,
  calendar,
  certificateOfCompletion,
  changeOrder,
  channel,
  checklist,
  colorSelection,
  complianceEvent,
  contractorDocumentExemption,
  contractorDocuments,
  contractorOwners,
  contractorInternalFields,
  contractorInternalDetailFields,
  contractorProfile,
  contractorTraining,
  conversationAssignment,
  conversationClose,
  conversationSubscription,
  conversation,
  county,
  creditMemo,
  deadline,
  debug,
  deletedTask,
  dropboxSign,
  escalation,
  escalationHub,
  externalContractor,
  file,
  fileAnnotation,
  finalInspection,
  financeAdminTools,
  funding,
  geographicArea,
  globalAppSettings,
  hic,
  holdup,
  homeownerPortal,
  homeownerSentiment,
  hubspot,
  impersonation,
  inbox,
  invoice,
  jobDetails,
  journalEntry,
  laborbook,
  lineItemPrice,
  link,
  livePhotoReview,
  market,
  message,
  marketingLead,
  mcp,
  measurementReport,
  navigation,
  notificationSettings,
  notifications,
  notificationList,
  partnerFlow,
  source,
  sowChange,
  materials,
  ntp,
  opsAssignment,
  organization,
  organizationSettings,
  permission,
  permittingEntity,
  person,
  personGroup,
  personToContractor,
  phaseDuration,
  phoneProvider,
  project,
  projectCategory,
  projectCheckIn,
  projectChecklistSchema,
  projectMilestones,
  projectStatus,
  projectTable,
  propertyAddress,
  proxyPhone,
  quote,
  quoteTab,
  receipt,
  recruitingTriage,
  requestForQuote,
  review,
  roofScope,
  salesDetails,
  scopeOfWorkType,
  contractorLicense,
  smsMessage,
  serviceArea,
  serviceAreaCapacity,
  serviceAreaPause,
  sunrunAutomation,
  task,
  taskAssignee,
  taskDueDate,
  taskReset,
  taskStatus,
  tag,
  ticket,
  region,
  regionRole,
  user,
  vendorCredit,
  view,
  viewList,
  workOrder,
  workflow,
  reviewBidGuide,
  reviewRevenueAutoComplete,
  verifyContractorCostsAutoComplete,
  loanRateSheet,
  insurance,
  estimate,
  ratebook,
  remittance,
  worksite,
  worksiteView,
};

export { hasCapability } from "./src/base";
export {
  AMFAM_AUTOMATIONS_USER,
  AUTONOMOUS_AGENT_USER,
  AUTONOMOUS_AGENT_USER_ID,
  ENSITE_AUTOMATIONS_USER,
  GROOVE_USER,
  INSPECTIFY_AUTOMATIONS_USER,
  OPEN_PHONE_USER,
  OPENLY_AUTOMATIONS_USER,
  PARTNER_BOT_USER,
  REMI_AI_USER,
  REMI_AUTOMATIONS_USER,
  REMI_AUTOMATIONS_USER_ID,
  REMI_FICTIONAL_USER_IDS,
  REMI_HUBSPOT_USER,
  REMI_WEBSITE_USER,
  SALES_AGENT_USER,
  SALES_USER,
  SUNRUN_AUTOMATIONS_USER,
  SUPPORT_USER,
  UNMAPPED_QUO_SENDER_USER,
  UNMAPPED_QUO_SENDER_USER_ID,
  WESTHILL_AUTOMATIONS_USER,
} from "./src/constant-users";
export { CONTEXT_PERSON_SELECT, type ContextPerson, type ContextUser } from "./src/context-user";
export * from "./src/role-capability-mapping";
export * from "./src/utils/account-management-override";
export * from "./src/utils/invitation";
export * from "./src/utils/lists";
export * from "./src/utils/nomad-job-types";
export * from "./src/utils/partner-only";
export * from "./src/utils/remi-org-restricted-roles";
export * from "./src/utils/types";
export type PermissionUser = User;
